import React, { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch,
  TextField, Typography, Alert, CircularProgress, InputAdornment,
  IconButton, Tooltip, Paper, Stack, Divider,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add, Edit, PowerSettingsNew, Search, Inventory2,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { inventoryApi } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  InventoryItem, InventoryCreate, InventoryCategory, INVENTORY_CATEGORY_LABELS,
} from '../types/index.ts';

const GBP = (v: number) => `£${v.toFixed(2)}`;

const DEFAULT_FORM: InventoryCreate = {
  name: '',
  manufacturer: '',
  model_number: '',
  category: InventoryCategory.HEAT_PUMP,
  capacity_kw: undefined,
  cop: undefined,
  unit_price: 0,
  cost_price: undefined,
  vat_rate: 5,
  in_stock: true,
  stock_quantity: undefined,
  notes: '',
  is_active: true,
};

const CATEGORY_COLORS: Record<InventoryCategory, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [InventoryCategory.HEAT_PUMP]: 'primary',
  [InventoryCategory.CYLINDER]: 'info',
  [InventoryCategory.CONTROLS]: 'secondary',
  [InventoryCategory.SOLAR_PANEL]: 'warning',
  [InventoryCategory.BATTERY]: 'success',
  [InventoryCategory.EV_CHARGER]: 'error',
  [InventoryCategory.OTHER]: 'default',
};

export default function InventoryPage() {
  const { isManager } = useAuth();
  const queryClient = useQueryClient();

  const [categoryFilter, setCategoryFilter] = useState<InventoryCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<InventoryItem | null>(null);
  const [form, setForm] = useState<InventoryCreate>(DEFAULT_FORM);
  const [formError, setFormError] = useState('');

  const { data, isLoading, error } = useQuery(
    ['inventory', categoryFilter, search, showInactive],
    () => inventoryApi.getInventory({
      category: categoryFilter === 'all' ? undefined : categoryFilter,
      search: search || undefined,
      active_only: !showInactive,
    }),
    { keepPreviousData: true },
  );

  const extractError = (e: any): string => {
    const detail = e.response?.data?.detail;
    if (!detail) return 'An unexpected error occurred';
    // Pydantic v2 returns an array of {loc, msg, type} objects on 422
    if (Array.isArray(detail)) {
      return detail.map((d: any) => {
        const field = d.loc?.slice(1).join(' → ') || '';
        return field ? `${field}: ${d.msg}` : d.msg;
      }).join('; ');
    }
    return String(detail);
  };

  const createMutation = useMutation(inventoryApi.createInventoryItem, {
    onSuccess: () => { queryClient.invalidateQueries('inventory'); closeDialog(); },
    onError: (e: any) => setFormError(extractError(e)),
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: InventoryCreate }) => inventoryApi.updateInventoryItem(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries('inventory'); closeDialog(); },
      onError: (e: any) => setFormError(extractError(e)),
    },
  );

  const retireMutation = useMutation(inventoryApi.deleteInventoryItem, {
    onSuccess: () => queryClient.invalidateQueries('inventory'),
  });

  const openCreate = () => {
    setEditItem(null);
    setForm(DEFAULT_FORM);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (item: InventoryItem) => {
    setEditItem(item);
    setForm({
      name: item.name,
      manufacturer: item.manufacturer,
      model_number: item.model_number || '',
      category: item.category,
      capacity_kw: item.capacity_kw,
      cop: item.cop,
      unit_price: item.unit_price,
      cost_price: item.cost_price,
      vat_rate: item.vat_rate,
      in_stock: item.in_stock,
      stock_quantity: item.stock_quantity,
      notes: item.notes || '',
      is_active: item.is_active,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setEditItem(null); };

  const handleSubmit = () => {
    if (!form.name || !form.manufacturer || !form.unit_price) {
      setFormError('Name, manufacturer and unit price are required');
      return;
    }
    if (editItem) {
      updateMutation.mutate({ id: editItem.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const setField = (field: keyof InventoryCreate, value: any) =>
    setForm(f => ({ ...f, [field]: value }));

  const columns: GridColDef[] = [
    {
      field: 'name', headerName: 'Product', flex: 2, minWidth: 200,
      renderCell: (p: GridRenderCellParams<InventoryItem>) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{p.row.name}</Typography>
          <Typography variant="caption" color="text.secondary">{p.row.manufacturer} {p.row.model_number && `· ${p.row.model_number}`}</Typography>
        </Box>
      ),
    },
    {
      field: 'category', headerName: 'Category', width: 180,
      renderCell: (p: GridRenderCellParams<InventoryItem>) => (
        <Chip
          label={p.row.category_display}
          color={CATEGORY_COLORS[p.row.category]}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'capacity_kw', headerName: 'Capacity', width: 110,
      renderCell: (p: GridRenderCellParams<InventoryItem>) =>
        p.row.capacity_kw ? `${p.row.capacity_kw} kW` : '—',
    },
    {
      field: 'cop', headerName: 'CoP', width: 80,
      renderCell: (p: GridRenderCellParams<InventoryItem>) =>
        p.row.cop ? p.row.cop.toFixed(1) : '—',
    },
    {
      field: 'unit_price', headerName: 'Price (ex VAT)', width: 140,
      renderCell: (p: GridRenderCellParams<InventoryItem>) => (
        <Box>
          <Typography variant="body2">{GBP(p.row.unit_price)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {GBP(p.row.unit_price_inc_vat)} inc {p.row.vat_rate}% VAT
          </Typography>
        </Box>
      ),
    },
    {
      field: 'in_stock', headerName: 'Stock', width: 110,
      renderCell: (p: GridRenderCellParams<InventoryItem>) => (
        <Chip
          label={p.row.in_stock ? (p.row.stock_quantity != null ? `${p.row.stock_quantity} units` : 'In stock') : 'Out of stock'}
          color={p.row.in_stock ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'is_active', headerName: 'Status', width: 90,
      renderCell: (p: GridRenderCellParams<InventoryItem>) => (
        <Chip label={p.row.is_active ? 'Active' : 'Retired'} color={p.row.is_active ? 'default' : 'error'} size="small" />
      ),
    },
    ...(isManager ? [{
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: (p: GridRenderCellParams<InventoryItem>) => (
        <Stack direction="row">
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(p.row)}><Edit fontSize="small" /></IconButton>
          </Tooltip>
          {p.row.is_active && (
            <Tooltip title="Retire item">
              <IconButton size="small" color="error" onClick={() => retireMutation.mutate(p.row.id)}>
                <PowerSettingsNew fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    }] : []),
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2 color="primary" />
          <Typography variant="h5" fontWeight={600}>Inventory</Typography>
        </Box>
        {isManager && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Add Item
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search name, manufacturer…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 240 }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label="All"
              variant={categoryFilter === 'all' ? 'filled' : 'outlined'}
              onClick={() => setCategoryFilter('all')}
              color={categoryFilter === 'all' ? 'primary' : 'default'}
            />
            {Object.values(InventoryCategory).map(cat => (
              <Chip
                key={cat}
                label={INVENTORY_CATEGORY_LABELS[cat]}
                variant={categoryFilter === cat ? 'filled' : 'outlined'}
                color={categoryFilter === cat ? CATEGORY_COLORS[cat] : 'default'}
                onClick={() => setCategoryFilter(cat)}
              />
            ))}
          </Stack>
          {isManager && (
            <FormControlLabel
              control={<Switch checked={showInactive} onChange={e => setShowInactive(e.target.checked)} size="small" />}
              label="Show retired"
              sx={{ ml: 'auto' }}
            />
          )}
        </Stack>
      </Paper>

      {/* Grid */}
      {error ? (
        <Alert severity="error">Failed to load inventory</Alert>
      ) : (
        <Paper>
          <DataGrid
            rows={data?.items || []}
            columns={columns}
            rowCount={data?.total || 0}
            loading={isLoading}
            autoHeight
            disableRowSelectionOnClick
            getRowHeight={() => 60}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
            sx={{ border: 'none' }}
          />
        </Paper>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Item' : 'Add Inventory Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <Stack direction="row" spacing={2}>
              <TextField label="Product name *" value={form.name} onChange={e => setField('name', e.target.value)} fullWidth size="small" />
              <TextField label="Manufacturer *" value={form.manufacturer} onChange={e => setField('manufacturer', e.target.value)} fullWidth size="small" />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField label="Model number" value={form.model_number || ''} onChange={e => setField('model_number', e.target.value)} fullWidth size="small" />
              <FormControl fullWidth size="small">
                <InputLabel>Category *</InputLabel>
                <Select label="Category *" value={form.category} onChange={e => setField('category', e.target.value)}>
                  {Object.values(InventoryCategory).map(cat => (
                    <MenuItem key={cat} value={cat}>{INVENTORY_CATEGORY_LABELS[cat]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                label="Capacity (kW)"
                type="number"
                value={form.capacity_kw ?? ''}
                onChange={e => setField('capacity_kw', e.target.value ? parseFloat(e.target.value) : undefined)}
                fullWidth size="small"
                helperText="Heat pumps / solar"
              />
              <TextField
                label="CoP"
                type="number"
                value={form.cop ?? ''}
                onChange={e => setField('cop', e.target.value ? parseFloat(e.target.value) : undefined)}
                fullWidth size="small"
                helperText="Heat pumps only"
              />
            </Stack>

            <Divider><Typography variant="caption" color="text.secondary">Pricing</Typography></Divider>

            <Stack direction="row" spacing={2}>
              <TextField
                label="Unit price (ex VAT) *"
                type="number"
                value={form.unit_price || ''}
                onChange={e => setField('unit_price', parseFloat(e.target.value))}
                fullWidth size="small"
                InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
              />
              <TextField
                label="Cost price (internal)"
                type="number"
                value={form.cost_price ?? ''}
                onChange={e => setField('cost_price', e.target.value ? parseFloat(e.target.value) : undefined)}
                fullWidth size="small"
                InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                helperText="Not shown to customers"
              />
            </Stack>

            <TextField
              label="VAT rate (%)"
              type="number"
              value={form.vat_rate}
              onChange={e => setField('vat_rate', parseFloat(e.target.value))}
              size="small"
              sx={{ width: 160 }}
              helperText="5% heat pumps · 20% EV chargers"
            />

            <Divider><Typography variant="caption" color="text.secondary">Stock</Typography></Divider>

            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={<Switch checked={form.in_stock} onChange={e => setField('in_stock', e.target.checked)} />}
                label="In stock"
              />
              <TextField
                label="Quantity"
                type="number"
                value={form.stock_quantity ?? ''}
                onChange={e => setField('stock_quantity', e.target.value ? parseInt(e.target.value) : undefined)}
                size="small"
                sx={{ width: 120 }}
                disabled={!form.in_stock}
              />
            </Stack>

            <TextField
              label="Notes"
              value={form.notes || ''}
              onChange={e => setField('notes', e.target.value)}
              multiline rows={2}
              fullWidth size="small"
            />

            {editItem && (
              <FormControlLabel
                control={<Switch checked={form.is_active} onChange={e => setField('is_active', e.target.checked)} />}
                label="Active (uncheck to retire)"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isLoading || updateMutation.isLoading}
            startIcon={(createMutation.isLoading || updateMutation.isLoading) ? <CircularProgress size={16} /> : undefined}
          >
            {editItem ? 'Save changes' : 'Add item'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
